import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useMemo,
} from 'react'
import { LINKER_MODEL_IDS } from 'src/lib/Linkers/Linkers.constants'
import { LinkerError } from 'src/lib/Linkers/Linkers.components'
import { FileSelector } from 'src/components/Reusable/Forms/FileSelector/FileSelector'
import { stripFileExtension } from 'src/components/Reusable/Forms/FileSelector/FileSelector.helpers'
import {
  useHasReceptor,
  useReceptor,
  useReceptors,
} from 'src/pages/Receptors/receptors.hooks'
import { TextButton } from '../../Reusable/Buttons/TextButton'
import { DocumentFormData, DocumentType } from './AddDocumentForm'
import {
  PolicyDocGoogleDriveButton,
  PolicyDocLinkTypeButton,
  PolicyDocSharepointButton,
  PolicyDocConfluenceButton,
  PolicyDocNotionButton,
} from './AddDocumentForm.components'

type DocumentTypeSelectorProps = {
  setFormData: Dispatch<SetStateAction<DocumentFormData>>
  type: DocumentType
  setType: Dispatch<SetStateAction<DocumentType>>
  policyId: string
}

export const DocumentTypeSelector = ({
  setFormData,
  type,
  setType,
  policyId,
}: DocumentTypeSelectorProps): JSX.Element => {
  const [gdriveError, setGdriveError] = useState<string>('')

  const { data: HasConfluenceReceptor } = useHasReceptor(
    'trr-confluence-dms',
    true,
  )
  const { data: HasSharepointReceptor } = useHasReceptor('trr-sharepoint', true)
  const { data: HasGdriveReceptor } = useHasReceptor('trr-gdrive', true)
  const hasConfluenceReceptor = useMemo(
    () => HasConfluenceReceptor?.getHasReceptor() || false,
    [HasConfluenceReceptor],
  )
  const hasSharepointReceptor = useMemo(
    () => HasSharepointReceptor?.getHasReceptor() || false,
    [HasSharepointReceptor],
  )
  const hasGdriveReceptor = useMemo(
    () => HasGdriveReceptor?.getHasReceptor() || false,
    [HasGdriveReceptor],
  )
  const { data: confReceptor } = useReceptor({
    modelId: 'trr-confluence-dms',
    shouldFetch: hasConfluenceReceptor,
  })
  const { data: spReceptor } = useReceptor({
    modelId: 'trr-sharepoint',
    shouldFetch: hasSharepointReceptor,
  })
  // Several Google Drive connections can exist; any enabled one offers the
  // button, where a single-row lookup would report whichever row came first.
  const { data: gdriveReceptors } = useReceptors(
    'trr-gdrive',
    hasGdriveReceptor,
  )
  const isConfluenceConnected = useMemo(() => {
    if (!confReceptor) return false
    return confReceptor.getIsenabled()
  }, [confReceptor])

  const isSharepointConnected = useMemo(() => {
    if (!spReceptor) return false
    return spReceptor.getIsenabled()
  }, [spReceptor])
  const isGdriveConnected = useMemo(
    () =>
      (gdriveReceptors?.getReceptorsList() || []).some((r) => r.getIsenabled()),
    [gdriveReceptors],
  )
  const { data: HasNotionReceptor } = useHasReceptor('trr-notion', true)
  const hasNotionReceptor = useMemo(
    () => HasNotionReceptor?.getHasReceptor() || false,
    [HasNotionReceptor],
  )
  const { data: notionReceptors } = useReceptors(
    'trr-notion',
    hasNotionReceptor,
  )
  const isNotionConnected = useMemo(
    () =>
      (notionReceptors?.getReceptorsList() || []).some((r) => r.getIsenabled()),
    [notionReceptors],
  )

  const clearText = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      setFormData((state) => {
        const newState: DocumentFormData = {
          ...state,
          mime: '',
          body: '',
          caption: '',
        }
        return newState
      })
      if (type !== DocumentType.LINK) setType(DocumentType.NULL)
    },
    [setFormData, type, setType],
  )

  if (type !== DocumentType.NULL) {
    return <TextButton onClick={clearText}>Clear</TextButton>
  }

  const handleFileChange = (files: File[]) => {
    if (files.length === 0) return
    const file = files[0]
    const fileType = file.type

    setFormData((state) => {
      const newState: DocumentFormData = {
        ...state,
        mime: fileType,
        body: file,
        caption: stripFileExtension(file.name),
      }
      return newState
    })
    setType(DocumentType.FILE)
  }

  let alternateTypeOptions = [
    <PolicyDocLinkTypeButton
      setFormData={setFormData}
      setType={setType}
      key={'link'}
    />,
  ]

  if (isGdriveConnected) {
    alternateTypeOptions = [
      ...alternateTypeOptions,
      <PolicyDocGoogleDriveButton
        policyId={policyId}
        setGdriveError={setGdriveError}
        key={'gdrive-connected'}
      />,
    ]
  }

  if (isSharepointConnected) {
    alternateTypeOptions = [
      ...alternateTypeOptions,
      <PolicyDocSharepointButton
        key={'sharepoint'}
        setFormData={setFormData}
        setType={setType}
      />,
    ]
  }

  if (isConfluenceConnected) {
    alternateTypeOptions = [
      ...alternateTypeOptions,
      <PolicyDocConfluenceButton
        policyId={policyId}
        key={'confluence'}
        setFormData={setFormData}
        setType={setType}
      />,
    ]
  }

  if (isNotionConnected) {
    alternateTypeOptions = [
      ...alternateTypeOptions,
      <PolicyDocNotionButton policyId={policyId} key={'notion'} />,
    ]
  }

  return (
    <>
      <FileSelector
        alternateTypeOptions={alternateTypeOptions}
        onChange={handleFileChange}
        isLoading={false}
        isValid={true}
        errorMessage={''}
      />
      {gdriveError && (
        <LinkerError
          errMsg={gdriveError}
          linkerId={LINKER_MODEL_IDS.GOOGLE_DRIVE}
        />
      )}
    </>
  )
}
